import "./SectionScoreCard.css";

import { type FC, useId } from "react";
import { twMerge } from "tailwind-merge";

import { AssessmentCategory } from "api/api-types-assessment";
import { SECTION_SETTINGS_GRADE_TAB_INDEX } from "components/modals/SectionSettingsModal";
import { SectionScoreDisplayFormat } from "components/server-types";
import { t } from "i18n/i18n";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { CircleInfoIcon, ScoreIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentOrg,
  selectCurrentSection,
  selectCurrentUserId,
  userSelectors,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { getMaxSectionScoreForOrg, getRubricScoreFromPercent } from "utils/assessment";
import { useSectionScoreTable } from "utils/hooks/use-section-score-table";
import { getUserFullName } from "utils/user-utils";

type SectionScoreCardSectionProps = {
  actualValue: string | null;
  className?: string;
  isSectionScore?: boolean;
  maxValue: string;
  shouldShowValues: boolean;
  title: string;
  titleTooltip?: string;
  userId?: string | null;
  weight?: number;
};

const GradeSettingsButton: FC = () => {
  const dispatch = useAppDispatch();
  const isMdOrLarger = useIsMdOrLarger();

  return (
    <SquareButton
      className="shrink-0"
      kind="tertiary"
      size="s"
      onClick={() => {
        dispatch(viewStateActions.setSectionSettingsTabIndex(SECTION_SETTINGS_GRADE_TAB_INDEX));
      }}
    >
      {isMdOrLarger
        ? t("assessment_dashboard.grades.settings")
        : t("assessment_dashboard.grades.settings_short")}
    </SquareButton>
  );
};

const SectionScoreCardSection: FC<SectionScoreCardSectionProps> = ({
  actualValue,
  className,
  isSectionScore,
  maxValue,
  shouldShowValues,
  title,
  titleTooltip,
  userId,
  weight,
}) => {
  const currentOrg = useAppSelector(selectCurrentOrg);
  const { canAuthorSection } = useCapabilities();
  const id = useId();

  const sectionScoreDisplayFormat =
    currentOrg?.section_score_display_format || SectionScoreDisplayFormat.Percent;
  const isPercent = sectionScoreDisplayFormat === SectionScoreDisplayFormat.Percent;

  const tooltipRole = canAuthorSection && !userId ? "instructor" : "student";
  const tooltipKey = isSectionScore ? sectionScoreDisplayFormat : "category";
  const tooltipText = t(`assessment_dashboard.tooltip.${tooltipRole}.${tooltipKey}`);

  const showActualValue = shouldShowValues && actualValue !== null;
  const scoreIconSize = isSectionScore ? "l" : "s";
  const textClass = isSectionScore ? "text-lg" : "text-sm";
  const placeholderClass = "shrink-0 whitespace-nowrap font-normal text-black-tint-20";
  const showSettingsButton = isSectionScore && canAuthorSection && !userId;

  const actualScore = showActualValue ? (
    isPercent ? (
      <span
        className={twMerge(
          "shrink-0 font-semibold text-blue",
          isSectionScore ? "text-2xl" : "text-base",
        )}
      >
        {actualValue}%
      </span>
    ) : (
      <div className="flex shrink-0 items-center gap-1 whitespace-nowrap">
        <ScoreIcon className={textClass} size={scoreIconSize} value={Number(actualValue)} />

        <span className="text-xs font-normal text-black-tint-40"> / {maxValue}</span>
      </div>
    )
  ) : null;

  const scoreDisplay = showActualValue ? (
    <TooltipTrigger content={tooltipText}>{actualScore}</TooltipTrigger>
  ) : (
    <span className={placeholderClass}>{t("assessment_dashboard.grades.not_yet_assessed")}</span>
  );

  const titleLabel = (
    <label
      className={twMerge(
        "min-w-1 shrink truncate",
        isSectionScore ? "text-xl font-semibold text-blue" : "text-sm font-normal",
        weight != null && "text-blue",
      )}
      htmlFor={id}
    >
      {title}
    </label>
  );

  const titleWithActions = (
    <div className="flex min-w-1 shrink items-center gap-2">
      {titleLabel}

      {titleTooltip && (
        <TooltipTrigger content={titleTooltip} label={titleTooltip}>
          <CircleInfoIcon />
        </TooltipTrigger>
      )}

      {showSettingsButton && <GradeSettingsButton />}
    </div>
  );

  return (
    <div className={twMerge("flex min-w-1 shrink flex-col gap-2", className)}>
      {weight != null ? (
        <div className="flex min-w-1 shrink items-end justify-between gap-1">
          <div className="flex min-w-1 shrink flex-col">
            {titleLabel}

            <div className="body-xs min-w-1 shrink font-normal text-black-tint-55">
              {t("assessment_dashboard.grades.worth_of_total", { weight })}
            </div>
          </div>

          {scoreDisplay}
        </div>
      ) : (
        <div className="flex min-w-1 shrink flex-row items-center justify-between gap-1">
          {titleWithActions}

          {scoreDisplay}
        </div>
      )}

      <meter
        className="h-3 w-full"
        id={id}
        max={maxValue}
        value={(shouldShowValues && actualValue) || "0"}
      />
    </div>
  );
};

type SectionScoreCardProps = {
  /**
   * If `userId` is specified, this component will show the grade for the given user ID;
   * otherwise, it will show the average grade for the entire class.
   * Only instructors can specify this prop; for students, it is ignored.
   */
  userId?: string | null;
};

// At the moment the back end calculates score data for each student in a given Section,
// but doesn't calculate overall averages across the students, so we have to do that here.
const calculateMeanScore = (
  values: (string | null)[],
  maxValue: number,
  { asPercent = false }: { asPercent?: boolean } = {},
): string | null => {
  if (maxValue === 0) return null;

  const populatedValues = values.filter((val) => val !== null);
  if (populatedValues.length === 0) return null;

  const totalActualValue = populatedValues.reduce((acc, val) => {
    const n = Number(val);
    return acc + (asPercent ? (n / maxValue) * 100 : n);
  }, 0);

  return (totalActualValue / populatedValues.length).toFixed(1).replace(".0", "");
};

export const SectionScoreCard: FC<SectionScoreCardProps> = ({ userId: userIdProp = null }) => {
  const currentOrg = useAppSelector(selectCurrentOrg);
  const section = useAppSelector(selectCurrentSection);
  const { canAuthorSection } = useCapabilities();

  // If the current user is a student, they are not allowed to view the grade for any other students.
  // Therefore, if the `userId` prop is not provided, we use the current user ID.
  // Instructors are allowed to view the grades for all students, so they can pass a specific `userId`.
  const currentUserId = useAppSelector(selectCurrentUserId);
  const userId = (canAuthorSection ? userIdProp : currentUserId) || null;
  const user = useAppSelector((s) => userSelectors.selectById(s, userId));

  // Use the SectionScore data that was already fetched from the server by SectionScoreTableProvider.
  const { rows: sectionScoreTableRows } = useSectionScoreTable();

  if (!currentOrg || !section || !sectionScoreTableRows) {
    return null;
  }

  // If a userId was passed in, filter the table to just the one row matching that user.
  const filteredTableRows = userId
    ? sectionScoreTableRows.filter((row) => row.student_id === userId)
    : sectionScoreTableRows;

  const meanPrepWorkScore = calculateMeanScore(
    filteredTableRows.map((item) => item.prep_work_score),
    section.section_score_prepwork_percentage,
    { asPercent: true },
  );

  const meanActivitiesScore = calculateMeanScore(
    filteredTableRows.map((item) => item.activities_score),
    section.section_score_activities_percentage,
    { asPercent: true },
  );

  const meanAssignmentsScore = calculateMeanScore(
    filteredTableRows.map((item) => item.assignments_score),
    section.section_score_assignments_percentage,
    { asPercent: true },
  );

  const maxTotalScore = getMaxSectionScoreForOrg(currentOrg);
  const isPercent = currentOrg.section_score_display_format === SectionScoreDisplayFormat.Percent;
  const meanTotalScore = calculateMeanScore(
    filteredTableRows.map((item) => item.total_section_score),
    maxTotalScore,
  );

  const toCategoryDisplayScore = (percent: string | null): string | null => {
    if (percent === null || isPercent) return percent;
    const rubricScore = getRubricScoreFromPercent(Number(percent), currentOrg.rubric);
    return rubricScore == null ? null : rubricScore.toFixed(1).replace(".0", "");
  };

  // The score components for each AssessmentCategory are displayed in the following order.
  const scoreComponentsByCategory = (
    [
      [
        AssessmentCategory.prepwork,
        toCategoryDisplayScore(meanPrepWorkScore),
        section.section_score_prepwork_percentage,
      ],
      [
        AssessmentCategory.activities,
        toCategoryDisplayScore(meanActivitiesScore),
        section.section_score_activities_percentage,
      ],
      [
        AssessmentCategory.assignments,
        toCategoryDisplayScore(meanAssignmentsScore),
        section.section_score_assignments_percentage,
      ],
    ] as const
  ).filter(([, , weight]) => weight > 0);

  const assessedCategoryCount = scoreComponentsByCategory.filter(
    ([, actualValue]) => actualValue !== null,
  ).length;
  const hasPartialCategoryAssessment =
    assessedCategoryCount > 0 && assessedCategoryCount < scoreComponentsByCategory.length;

  const titleTooltip = hasPartialCategoryAssessment
    ? t("assessment_dashboard.tooltip.partial_categories")
    : undefined;

  // We should only show the values if the section scores are published,
  // or if the user can author the section (in which case they can see the unpublished values).
  const shouldShowValues = !!section.section_score_published_at || canAuthorSection;

  const title = canAuthorSection
    ? user
      ? t("assessment_dashboard.grades.title_name", {
          name: getUserFullName(user),
        })
      : t("assessment_dashboard.grades.title_average")
    : t("assessment_dashboard.grades.title_own");

  return (
    <div className="section-score-card flex w-full flex-col gap-3 rounded-xl bg-blue-tint-95 p-6 pt-5">
      <div className="flex flex-col gap-4">
        <div className="pb-6">
          <SectionScoreCardSection
            actualValue={meanTotalScore}
            maxValue={maxTotalScore.toFixed()}
            shouldShowValues={shouldShowValues}
            title={title}
            titleTooltip={titleTooltip}
            userId={userId}
            isSectionScore
          />
        </div>

        <div className="flex flex-col gap-4 md:flex-row md:gap-6">
          {scoreComponentsByCategory.map(([category, actualValue, weight]) => (
            <div className="min-w-1 flex-1" key={category}>
              <SectionScoreCardSection
                actualValue={actualValue}
                maxValue={maxTotalScore.toFixed()}
                shouldShowValues={shouldShowValues}
                title={t(`assessment_dashboard.grades.${category}`)}
                userId={userId}
                weight={weight}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
